/**
 * A return's lifecycle, as the screens offer it (FR-5, FR-E17 — e2e-filing C-02).
 *
 * One copy for Returns and GSTR-3B. Until C-02 the buttons lived inline in `Periods.tsx` and
 * looked for the *client workbook's* hash whatever the return was, so a GSTR-3B could never be
 * recorded as approved from a screen: it binds its working paper, not a workbook. The rules here
 * are pure so `tests/the-3b-period.test.ts` can hold them without rendering.
 */

import type { PeriodRecord } from './api'

export type LifecycleAction =
  | 'prepare'
  | 'sign_off'
  | 'send_for_approval'
  | 'approve'
  | 'record_filing'
  | 'lock'

/**
 * The artifact whose hash a return binds at send-for-approval, and whose current version the
 * approval must present (FR-7): GSTR-1's client workbook; GSTR-3B's working paper (ADR-0031),
 * which is what the client sees today — the 3B JSON and utility are C-09's.
 */
export function boundArtifactKind(returnType: string): string {
  return returnType === 'GSTR3B' ? 'gstr3b_working' : 'client_workbook'
}

/** The sha256 the approval presents, from the period's artifact list. */
export function boundSha(
  period: Pick<PeriodRecord, 'return_type'>,
  artifacts: Array<{ kind: string; status: string; sha256: string }>,
): string | undefined {
  const kind = boundArtifactKind(period.return_type)
  return artifacts.find((a) => a.kind === kind && a.status === 'current')?.sha256
}

/** The preparer's fallback, named as what it is: their word that the client agreed. */
export const RECORD_APPROVAL_YOURSELF = "Record the client's approval yourself"

export interface OfferedAction {
  label: string
  action: LifecycleAction
  /** Sign-off asserts something, so it asks what was checked before it acts. */
  prompt?: boolean
}

/**
 * The transitions a return in this state offers, in the order the buttons show. The service is
 * what decides — a button it refuses shows its sentence — but a button that can only refuse is
 * noise, so `approve` needs the bound artifact's current hash and `record_filing` has its own
 * control (the acknowledgment number) rather than a bare button.
 */
export function offeredActions(
  period: Pick<PeriodRecord, 'state' | 'return_type'>,
  currentSha: string | undefined,
): OfferedAction[] {
  const out: OfferedAction[] = []
  if (period.state === 'open') out.push({ label: 'Prepare', action: 'prepare' })
  if (period.state === 'prepared') out.push({ label: 'Sign off', action: 'sign_off', prompt: true })
  if (period.state === 'prepared' || period.state === 'signed_off') {
    out.push({ label: 'Send for approval', action: 'send_for_approval' })
  }
  // ADR-0037 ND-E7 (FR-E15): the client approves through the link they were sent; this is the
  // fallback, and the service audits it as the preparer's word (`approved_by: preparer`).
  if (period.state === 'sent_for_approval' && currentSha) {
    out.push({ label: RECORD_APPROVAL_YOURSELF, action: 'approve' })
  }
  if (period.state === 'filed') out.push({ label: 'Lock', action: 'lock' })
  return out
}

/** Whether the screen shows the acknowledgment-number control (FR-9, step one). */
export function offersRecordFiling(period: Pick<PeriodRecord, 'state'>): boolean {
  return period.state === 'client_approved'
}

/**
 * The day a return was recorded filed, from its own history — the `filed` move's time, as
 * DD-MM-YYYY. Form GSTR-3B's 2(d) *Date of ARN* read "—" whatever the period held; this is the
 * date the practice recorded the acknowledgment, which is the day they filed. The portal's own
 * ARN date is in the filed return, and reading that is Wave E's. Null while not filed.
 */
export function filedOn(history: PeriodRecord['history'] | undefined): string | null {
  const move = (history ?? []).find(
    (h) => h.action === 'period.state_changed' && h.detail?.to === 'filed',
  )
  if (!move) return null
  const at = new Date(move.at)
  if (Number.isNaN(at.getTime())) return null
  const dd = String(at.getDate()).padStart(2, '0')
  const mm = String(at.getMonth() + 1).padStart(2, '0')
  return `${dd}-${mm}-${at.getFullYear()}`
}

/**
 * The months that have a GSTR-1 period and no GSTR-3B yet — what the 3B screen offers to open
 * on request (ND-E10 is open; "with the GSTR-1 automatically" is the other answer, and its seam
 * is one line in `api/periods.py::open_period`).
 */
export function monthsWithoutA3b(rows: Array<Pick<PeriodRecord, 'return_type' | 'period'>>): string[] {
  const have = new Set(rows.filter((r) => r.return_type === 'GSTR3B').map((r) => r.period))
  const months = rows.filter((r) => r.return_type === 'GSTR1' && !have.has(r.period)).map((r) => r.period)
  return [...new Set(months)]
}
